import { create } from "zustand";

export type WizardStepKey = "basics" | "brandKit" | "social" | "team" | "aiConfig";
export type StepStatus = "pending" | "complete" | "skipped";

export type WizardSteps = Record<WizardStepKey, StepStatus>;

const STEP_ORDER: WizardStepKey[] = [
  "basics",
  "brandKit",
  "social",
  "team",
  "aiConfig",
];

const DEFAULT_STEPS: WizardSteps = {
  basics: "pending",
  brandKit: "pending",
  social: "pending",
  team: "pending",
  aiConfig: "pending",
};

interface WizardStore {
  open: boolean;
  /** 0 = welcome, 1–5 = steps, 6 = complete */
  currentStep: number;
  steps: WizardSteps;

  openWizard: (atStep?: number) => void;
  closeWizard: () => void;
  goToStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  markStep: (key: WizardStepKey, status: "complete" | "skipped") => void;
  setSteps: (steps: Partial<WizardSteps>) => void;

  /** 0–100 — counts steps that are complete or skipped */
  completionPercent: () => number;
}

export const useWizardStore = create<WizardStore>((set, get) => ({
  open: false,
  currentStep: 0,
  steps: DEFAULT_STEPS,

  openWizard: (atStep = 0) => set({ open: true, currentStep: atStep }),
  closeWizard: () => set({ open: false }),
  goToStep: (step) => set({ currentStep: step }),

  nextStep: () => {
    const next = get().currentStep + 1;
    // Step 6 = completion screen (after step 5 = aiConfig)
    set({ currentStep: Math.min(next, 6) });
  },

  prevStep: () => {
    const prev = get().currentStep - 1;
    set({ currentStep: Math.max(prev, 1) }); // can't go before step 1
  },

  markStep: (key, status) => {
    set((s) => ({ steps: { ...s.steps, [key]: status } }));
  },

  setSteps: (incoming) => {
    set((s) => ({ steps: { ...s.steps, ...incoming } }));
  },

  completionPercent: () => {
    const { steps } = get();
    const done = STEP_ORDER.filter(
      (k) => steps[k] === "complete" || steps[k] === "skipped"
    ).length;
    return Math.round((done / STEP_ORDER.length) * 100);
  },
}));

export { STEP_ORDER };
